/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select, Checkbox, Spin } from 'antd'
import React from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'

type SelectInputProps = {
  label: string
  disabled: boolean
  options: any[]
  name: string
  value: string
  onChange: (value: string) => void
  fieldErrors: any
  customError: any
  showSearch?: boolean
  filterOption?: ((input: string, option: any) => boolean) | false
  style?: any
  extraCheckBox?: boolean
  extraCheckBoxLabel?: string
  extraCheckBoxOnChange?: (value: boolean) => void
  onSearch?: (value: string) => void
  allowClear?: boolean
  inputRef?: React.RefObject<any>
  onSelect?: (value: string) => void
  placeholder?: string
  notFoundContent?: React.ReactNode
  /** Tab index for keyboard navigation */
  tabIndex?: number
  backgroundColor?: string
  /** Whether dropdown width should match select width. Set to false to let dropdown expand to fit content */
  popupMatchSelectWidth?: boolean
  /** Custom render function for the selected label display */
  labelRender?: (props: {
    label: React.ReactNode
    value: string
  }) => React.ReactNode
  /** Callback when dropdown is scrolled - useful for infinite scroll pagination */
  onPopupScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  /** Show loading spinner in dropdown */
  loading?: boolean
  /** Controls whether the dropdown is open */
  open?: boolean
  /** Callback when dropdown visibility changes */
  onDropdownVisibleChange?: (open: boolean) => void
  /** Callback when selection is cleared */
  onClear?: () => void
  /** Custom suffix icon for the select */
  suffixIcon?: React.ReactNode
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  disabled,
  options,
  name,
  value,
  onChange,
  fieldErrors,
  customError,
  showSearch = false,
  filterOption,
  style,
  extraCheckBox,
  extraCheckBoxLabel,
  extraCheckBoxOnChange,
  onSearch,
  allowClear = true,
  inputRef,
  onSelect,
  placeholder,
  notFoundContent,
  tabIndex,
  backgroundColor = colors.SECONDARY_200,
  popupMatchSelectWidth = false,
  labelRender,
  onPopupScroll,
  loading,
  open,
  onDropdownVisibleChange,
  onClear,
  suffixIcon,
}) => {
  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${backgroundColor} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.WHITE}
          style={`
            align-self: stretch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          {label}
        </Text>
        {extraCheckBox && extraCheckBoxLabel && extraCheckBoxOnChange && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: flex-end;
            `}
          >
            <Checkbox
              onChange={(e) => extraCheckBoxOnChange(e.target.checked)}
              css={css`
                .ant-checkbox-inner {
                  background-color: ${colors.SECONDARY_200};
                  border-color: ${colors.WHITE};
                }
                .ant-checkbox-wrapper {
                  color: ${colors.WHITE};
                }
              `}
            >
              {extraCheckBoxLabel}
            </Checkbox>
          </div>
        )}
      </div>
      <Select
        popupMatchSelectWidth={popupMatchSelectWidth}
        placeholder={placeholder}
        ref={inputRef}
        allowClear={allowClear}
        disabled={disabled}
        showSearch={showSearch}
        onChange={(e) => onChange(e)}
        onSearch={(e) => onSearch && onSearch(e)}
        onSelect={onSelect}
        options={options}
        value={value || undefined}
        notFoundContent={notFoundContent}
        tabIndex={tabIndex}
        // @ts-ignore
        labelRender={labelRender}
        onPopupScroll={onPopupScroll}
        open={open}
        onDropdownVisibleChange={onDropdownVisibleChange}
        onClear={onClear}
        suffixIcon={suffixIcon}
        dropdownRender={(menu) => (
          <>
            {menu}
            {loading && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  padding: 8px;
                  border-top: 1px solid ${colors.SECONDARY_300};
                `}
              >
                <Spin size='small' />
              </div>
            )}
          </>
        )}
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          font-size: 14px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${backgroundColor} !important;
          border-radius: 8px !important;
          height: 40px !important;
          ${disabled && 'opacity: 0.5 !important'}
          ${style}
        `}
        filterOption={filterOption}
        status={
          fieldErrors[name] ||
          (Array.isArray(fieldErrors) && fieldErrors?.includes(name))
            ? 'error'
            : ''
        }
      />
      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={name}
      />
    </>
  )
}
